import React from 'react';
import AuthenticatedInput from '../AuthenticatedInput';
import { updateExamField } from '../../utils/examData';
import {
  calculateElbowFlexion, calculateElbowExtension, calculatePronation, calculateSupination,
} from '../TabRatingNarratives';
import { useExamTab } from './ExamTabContext';

const ElbowsTab: React.FC = () => {
  const {
    examData, setExamData, user, userProfile, startManualSession, setShowAuthModal,
    copyRegionValues, handleClearRegion, clearConfirmRegion, fillNormalValues,
  } = useExamTab();

  return (
                <div>
                  <h3 className="text-2xl font-semibold mb-6">Elbows</h3>

                  <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6 items-start">

                  {/* Elbow Rating Section — live WPI instrument (right column) */}
                  {examData?.elbows && (
                    <div className="lg:order-2 lg:sticky lg:top-20">
                      <div className="card p-4 flex flex-col gap-4">
                        <div>
                          <span className="font-medium">Right Elbow WPI:</span>
                          <span className="ml-2 text-xl font-bold">
                          {(() => {
                            const data = examData?.elbows;
                            if (!data) return "--";
                            
                            // Check if right elbow has ROM values
                            if (data.right_flexion === null && 
                                data.right_extension === null && 
                                data.right_pronation === null && 
                                data.right_supination === null) {
                              return "--";
                            }
                            
                            // Calculate individual impairments
                            const rightFlexion = calculateElbowFlexion(data.right_flexion ?? null);
                            const rightExtension = calculateElbowExtension(data.right_extension ?? null);
                            const rightPronation = calculatePronation(data.right_pronation ?? null);
                            const rightSupination = calculateSupination(data.right_supination ?? null);
                            
                            // Sum individual impairments
                            const rightElbowRating = rightFlexion + rightExtension + rightPronation + rightSupination;
                            
                            // Convert to Whole Person Impairment (60% of upper extremity rating)
                            const rightWPI = Math.round(rightElbowRating * 0.6);
                            return `${rightWPI}%`;
                          })()}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium">Left Elbow WPI:</span>
                          <span className="ml-2 text-xl font-bold">
                          {(() => {
                            const data = examData?.elbows;
                            if (!data) return "--";
                            
                            // Check if left elbow has ROM values
                            if (data.left_flexion === null && 
                                data.left_extension === null && 
                                data.left_pronation === null && 
                                data.left_supination === null) {
                              return "--";
                            }
                            
                            // Calculate individual impairments
                            const leftFlexion = calculateElbowFlexion(data.left_flexion ?? null);
                            const leftExtension = calculateElbowExtension(data.left_extension ?? null);
                            const leftPronation = calculatePronation(data.left_pronation ?? null);
                            const leftSupination = calculateSupination(data.left_supination ?? null);
                            
                            // Sum individual impairments
                            const leftElbowRating = leftFlexion + leftExtension + leftPronation + leftSupination;
                            
                            // Convert to Whole Person Impairment (60% of upper extremity rating)
                            const leftWPI = Math.round(leftElbowRating * 0.6);
                            return `${leftWPI}%`;
                          })()}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Range of Motion Section — left column */}
                  <div className="lg:order-1 mb-0">
                    <div className="flex justify-between items-center mb-4 border-b border-border pb-2">
                      <h4 className="text-lg font-medium">Range of Motion</h4>
                      <div className="flex gap-2 flex-wrap"><button onClick={() => copyRegionValues('elbows', 'right_to_left')} className="btn-secondary text-xs" title="Copy Right values to Left">R → L</button><button onClick={() => copyRegionValues('elbows', 'left_to_right')} className="btn-secondary text-xs" title="Copy Left values to Right">L → R</button><button onClick={() => handleClearRegion('elbows')} className={clearConfirmRegion === 'elbows' ? 'btn-danger text-xs' : 'btn-secondary text-xs'}>{clearConfirmRegion === 'elbows' ? 'Confirm Clear?' : 'Clear Values'}</button><button onClick={() => fillNormalValues('elbows')} className="btn-secondary text-xs" disabled={!user}>Fill Normal Values</button></div>
                    </div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-x-4 gap-y-4">
                      {/* Header Row */}
                      <div className="col-span-1"></div>
                      <div className="col-span-1 text-center text-xs font-medium text-content-tertiary hidden sm:block">Normal</div>
                      <div className="col-span-1 text-center font-medium">Right</div>
                      <div className="col-span-1 text-center font-medium">Left</div>

                      {/* Flexion Row */}
                      <div className="col-span-1 text-content-secondary">Flexion</div>
                      <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">140°</div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                          type="number"
                          className="input-field text-center"
                          value={examData?.elbows?.right_flexion ?? ''}
                          onChange={(value) => {
                            setExamData(prev => updateExamField(prev, 'elbows', 'right_flexion', value));
                          }}
                        />
                      </div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                          type="number"
                          className="input-field text-center"
                          value={examData?.elbows?.left_flexion ?? ''}
                          onChange={(value) => {
                            setExamData(prev => updateExamField(prev, 'elbows', 'left_flexion', value));
                          }}
                        />
                      </div>

                      {/* Extension Row */}
                      <div className="col-span-1 text-content-secondary">Extension</div>
                      <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">0°</div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                          type="number"
                          className="input-field text-center"
                          value={examData?.elbows?.right_extension ?? ''}
                          onChange={(value) => {
                            setExamData(prev => updateExamField(prev, 'elbows', 'right_extension', value));
                          }}
                        />
                      </div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                          type="number"
                          className="input-field text-center"
                          value={examData?.elbows?.left_extension ?? ''}
                          onChange={(value) => {
                            setExamData(prev => updateExamField(prev, 'elbows', 'left_extension', value));
                          }}
                        />
                      </div>

                      {/* Pronation Row */}
                      <div className="col-span-1 text-content-secondary">Pronation</div>
                      <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">80°</div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                          type="number"
                          className="input-field text-center"
                          value={examData?.elbows?.right_pronation ?? ''}
                          onChange={(value) => {
                            setExamData(prev => updateExamField(prev, 'elbows', 'right_pronation', value));
                          }}
                        />
                      </div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                          type="number"
                          className="input-field text-center"
                          value={examData?.elbows?.left_pronation ?? ''}
                          onChange={(value) => {
                            setExamData(prev => updateExamField(prev, 'elbows', 'left_pronation', value));
                          }}
                        />
                      </div>

                      {/* Supination Row */}
                      <div className="col-span-1 text-content-secondary">Supination</div>
                      <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">80°</div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                          type="number"
                          className="input-field text-center"
                          value={examData?.elbows?.right_supination ?? ''}
                          onChange={(value) => {
                            setExamData(prev => updateExamField(prev, 'elbows', 'right_supination', value));
                          }}
                        />
                      </div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                          type="number"
                          className="input-field text-center"
                          value={examData?.elbows?.left_supination ?? ''}
                          onChange={(value) => {
                            setExamData(prev => updateExamField(prev, 'elbows', 'left_supination', value));
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  </div>

                  {/* Elbow Rating Section was moved above the range of motion section and the narratives were moved to the Results Summary Box */}
                </div>
  );
};

export default ElbowsTab;
